import type { Filter, Sprite } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { TweenEasing } from "../../runner/Easing";
import { ExposureFilter } from "../../filters/ExposureFilter";
import type { SymbolLandEffect, SymbolLandAnimationRequest } from "./SymbolLandEffect";
import type { BrightnessLandTuning } from "./BrightnessLandTuning";

interface ActiveShine {
  sprite: Sprite;
  /** The sprite's filters before the swap — restored on finish so a pooled view never keeps the shine. */
  originalFilters: Filter[] | null;
  filter: ExposureFilter;
  /** Bumped to cancel an in-flight ramp (supersede / finishAll). */
  gen: number;
}

/**
 * A brightness shine when a targeted symbol lands: an `ExposureFilter` on the landed sprite ramps
 * 0 → peak → 0 on engine time (the drop-side analogue of the win shine); the sprite's own filters
 * come back when the ramp ends. Symbols outside `targetSymbolIds` (or without a sprite) are skipped.
 */
export class BrightnessSymbolLandEffect implements SymbolLandEffect {
  private readonly targetSymbolIds: ReadonlySet<string>;
  private readonly tuning: BrightnessLandTuning;
  private readonly taskRunner: PresentationTaskRunner;
  /** index → in-flight shine (a new landing on the cell, or `finishAll`, ends it). */
  private readonly active: Map<number, ActiveShine> = new Map();
  private generation = 0;

  public constructor(input: { targetSymbolIds: ReadonlySet<string>; tuning: BrightnessLandTuning; taskRunner: PresentationTaskRunner }) {
    this.targetSymbolIds = input.targetSymbolIds;
    this.tuning = input.tuning;
    this.taskRunner = input.taskRunner;
  }

  public play(request: SymbolLandAnimationRequest): void {
    const sprite = request.symbolSprite ?? null;
    if (!this.targetSymbolIds.has(request.symbolId) || !sprite || sprite.destroyed) return;
    this.finish(request.index); // supersede any in-flight shine on the same cell

    const filter = new ExposureFilter(0);
    const originalFilters = sprite.filters ? [...sprite.filters] : null;
    sprite.filters = [...(originalFilters ?? []), filter];
    const gen = ++this.generation;
    this.active.set(request.index, { sprite, originalFilters, filter, gen });

    const t = this.tuning;
    const isCurrent = (): boolean => !sprite.destroyed && this.active.get(request.index)?.gen === gen;
    void (async () => {
      if (t.startDelayMilliseconds > 0) {
        await this.taskRunner.sleep(t.startDelayMilliseconds / 1000);
        if (!isCurrent()) return;
      }
      await this.ramp(filter, isCurrent, 0, t.peak, t.rampUpSeconds, t.rampUpEasing);
      if (!isCurrent()) return;
      if (t.holdSeconds > 0) {
        await this.taskRunner.sleep(t.holdSeconds);
        if (!isCurrent()) return;
      }
      await this.ramp(filter, isCurrent, t.peak, 0, t.rampDownSeconds, t.rampDownEasing);
      if (!isCurrent()) return;
      this.complete(request.index);
    })();
  }

  public finishAll(): void {
    for (const index of Array.from(this.active.keys())) this.finish(index);
  }

  private finish(index: number): void {
    const entry = this.active.get(index);
    if (!entry) return;
    entry.gen = ++this.generation; // cancel any in-flight ramp for this cell
    this.complete(index);
  }

  /** Drop tracking and restore the sprite's filters. */
  private complete(index: number): void {
    const entry = this.active.get(index);
    if (!entry) return;
    this.active.delete(index);
    if (!entry.sprite.destroyed) entry.sprite.filters = entry.originalFilters;
    entry.filter.destroy();
  }

  /** Engine-time ramp of the exposure from → to (snaps when seconds ≤ 0). */
  private async ramp(filter: ExposureFilter, isCurrent: () => boolean, from: number, to: number, seconds: number, easing: TweenEasing): Promise<void> {
    if (seconds > 0) {
      await this.taskRunner.tweenProgress(
        seconds,
        (t01) => {
          if (isCurrent()) filter.highlight = from + (to - from) * t01;
        },
        undefined,
        { easing },
      );
    }
    if (isCurrent()) filter.highlight = to;
  }
}
